import { useMemo, useState, type ReactNode } from 'react';
import {
  AlertTriangle,
  CheckCircle2,
  ClipboardCheck,
  CopyCheck,
  FileQuestion,
  FileWarning,
  ListChecks,
  RefreshCw,
  ShieldCheck,
  SquareCheckBig,
  UserCheck,
  Users,
  XCircle,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { getLocalStorage, setLocalStorage } from '@/utils/localStorage';

type ValidationArea =
  | 'Client Data'
  | 'Carer Data'
  | 'Duplicates'
  | 'Documents'
  | 'Rota'
  | 'Billing'
  | 'Visits'
  | 'Tasks'
  | 'Expired Dates'
  | 'Final Checklist';

type ValidationSeverity = 'critical' | 'high' | 'medium' | 'low';
type ValidationStatus = 'open' | 'resolved';

interface ValidationIssue {
  id: string;
  area: ValidationArea;
  title: string;
  description: string;
  severity: ValidationSeverity;
  status: ValidationStatus;
  affectedRecord: string;
  action: string;
  blocker: boolean;
}

interface ChecklistItem {
  id: string;
  label: string;
  description: string;
  required: boolean;
}

const RESOLVED_KEY = 'fuchsia:admin-error-guard:resolved';
const CHECKLIST_KEY = 'fuchsia:admin-error-guard:checklist';

const validationIssues: ValidationIssue[] = [
  {
    id: 'client-phone-missing',
    area: 'Client Data',
    title: 'Client data validation',
    description: 'Client profile is missing one safe contact field or emergency contact confirmation.',
    severity: 'high',
    status: 'open',
    affectedRecord: 'Margaret Williams',
    action: 'Open client record and confirm phone, address, emergency contact and GP details.',
    blocker: true,
  },
  {
    id: 'carer-availability-missing',
    area: 'Carer Data',
    title: 'Carer data validation',
    description: 'Carer profile is missing availability or right-to-work evidence confirmation.',
    severity: 'high',
    status: 'open',
    affectedRecord: 'Daniel Moore',
    action: 'Update carer availability, documents, training and emergency contact before allocation.',
    blocker: true,
  },
  {
    id: 'duplicate-client',
    area: 'Duplicates',
    title: 'Duplicate detection',
    description: 'Two records have matching surname, postcode and date-of-birth pattern.',
    severity: 'medium',
    status: 'open',
    affectedRecord: 'George Taylor / G. Taylor',
    action: 'Review both records, merge only after manager confirmation and audit note.',
    blocker: false,
  },
  {
    id: 'missing-documents',
    area: 'Documents',
    title: 'Missing document detection',
    description: 'Mandatory carer document is missing from the file evidence list.',
    severity: 'high',
    status: 'open',
    affectedRecord: 'Amelia Patel - DBS evidence',
    action: 'Request document, upload evidence and mark verification date.',
    blocker: true,
  },
  {
    id: 'rota-incomplete',
    area: 'Rota',
    title: 'Incomplete rota detection',
    description: 'Weekend rota has visits without assigned carer.',
    severity: 'critical',
    status: 'open',
    affectedRecord: 'Saturday morning rota',
    action: 'Open scheduling, assign qualified carer and check travel/overtime impact.',
    blocker: true,
  },
  {
    id: 'invoice-no-visit',
    area: 'Billing',
    title: 'Invoice without visit detection',
    description: 'Invoice line exists without a matching completed visit record.',
    severity: 'critical',
    status: 'open',
    affectedRecord: 'INV-1004 medication support line',
    action: 'Hold invoice, link completed visit evidence or remove the line before sending.',
    blocker: true,
  },
  {
    id: 'visit-no-notes',
    area: 'Visits',
    title: 'Visit without notes detection',
    description: 'Completed visit has check-out but no care note or handover summary.',
    severity: 'medium',
    status: 'open',
    affectedRecord: 'Eileen Cooper - evening visit',
    action: 'Ask carer to complete visit note; do not close audit until note is present.',
    blocker: false,
  },
  {
    id: 'task-unassigned',
    area: 'Tasks',
    title: 'Unassigned task detection',
    description: 'Care task exists without assigned carer or visit slot.',
    severity: 'high',
    status: 'open',
    affectedRecord: 'Hydration and nutrition prompt',
    action: 'Assign task to a visit and confirm carer can see it in the portal.',
    blocker: true,
  },
  {
    id: 'expired-date',
    area: 'Expired Dates',
    title: 'Expired date detection',
    description: 'Training or policy date is expired and needs manager review.',
    severity: 'high',
    status: 'open',
    affectedRecord: 'Medication competency - expires this week',
    action: 'Book refresher, mark interim controls and update training evidence.',
    blocker: true,
  },
];

const finalChecklist: ChecklistItem[] = [
  {
    id: 'check-client-data',
    label: 'Client data validated',
    description: 'Client identity, address, GP, emergency contact and care risks are complete.',
    required: true,
  },
  {
    id: 'check-carer-data',
    label: 'Carer data validated',
    description: 'Carer contact, availability, training, documents and role permissions are complete.',
    required: true,
  },
  {
    id: 'check-duplicates',
    label: 'Duplicates reviewed',
    description: 'Potential duplicate clients/carers/invoices were reviewed before finalising.',
    required: true,
  },
  {
    id: 'check-rota',
    label: 'Rota complete',
    description: 'Every visit has a suitable carer, travel time and no unresolved conflict.',
    required: true,
  },
  {
    id: 'check-billing',
    label: 'Billing evidence matched',
    description: 'Every invoice line has a completed visit, approved hours and correct client/payer.',
    required: true,
  },
  {
    id: 'check-notes',
    label: 'Visit notes present',
    description: 'Completed visits include notes, tasks, concerns and handover where required.',
    required: true,
  },
  {
    id: 'check-expiry',
    label: 'Expired dates checked',
    description: 'Training, documents, policies and reviews are valid or escalated.',
    required: true,
  },
  {
    id: 'check-manager',
    label: 'Manager approval recorded',
    description: 'Sensitive or blocked items have human approval and audit notes.',
    required: true,
  },
];

const areaIcons: Record<ValidationArea, ReactNode> = {
  'Client Data': <Users className="h-5 w-5" />,
  'Carer Data': <UserCheck className="h-5 w-5" />,
  Duplicates: <CopyCheck className="h-5 w-5" />,
  Documents: <FileQuestion className="h-5 w-5" />,
  Rota: <ListChecks className="h-5 w-5" />,
  Billing: <FileWarning className="h-5 w-5" />,
  Visits: <ClipboardCheck className="h-5 w-5" />,
  Tasks: <SquareCheckBig className="h-5 w-5" />,
  'Expired Dates': <AlertTriangle className="h-5 w-5" />,
  'Final Checklist': <ShieldCheck className="h-5 w-5" />,
};

const severityStyles: Record<ValidationSeverity, string> = {
  critical: 'border-red-200 bg-red-50 text-red-950',
  high: 'border-amber-200 bg-amber-50 text-amber-950',
  medium: 'border-blue-200 bg-blue-50 text-blue-950',
  low: 'border-slate-200 bg-slate-50 text-slate-950',
};

export function AdministrativeErrorGuard() {
  const [resolvedIds, setResolvedIds] = useState<string[]>(() => getLocalStorage<string[]>(RESOLVED_KEY, []));
  const [checklist, setChecklist] = useState<Record<string, boolean>>(() =>
    getLocalStorage<Record<string, boolean>>(CHECKLIST_KEY, {})
  );
  const [area, setArea] = useState<'All' | ValidationArea>('All');
  const [lastScanAt, setLastScanAt] = useState(new Date().toISOString());

  const issues = useMemo(
    () =>
      validationIssues
        .map((issue) => ({
          ...issue,
          status: resolvedIds.includes(issue.id) ? 'resolved' as ValidationStatus : 'open' as ValidationStatus,
        }))
        .filter((issue) => area === 'All' || issue.area === area),
    [area, resolvedIds]
  );

  const openIssues = validationIssues.filter((issue) => !resolvedIds.includes(issue.id));
  const blockers = openIssues.filter((issue) => issue.blocker);
  const completedChecklist = finalChecklist.filter((item) => checklist[item.id]).length;
  const canFinalise = blockers.length === 0 && finalChecklist.every((item) => !item.required || checklist[item.id]);

  const markResolved = (issueId: string) => {
    setResolvedIds((current) => {
      const next = current.includes(issueId) ? current.filter((id) => id !== issueId) : [...current, issueId];
      setLocalStorage(RESOLVED_KEY, next);
      return next;
    });
  };

  const toggleChecklist = (itemId: string) => {
    setChecklist((current) => {
      const next = { ...current, [itemId]: !current[itemId] };
      setLocalStorage(CHECKLIST_KEY, next);
      return next;
    });
  };

  const runScan = () => {
    setLastScanAt(new Date().toISOString());
    toast.success(`${openIssues.length} issue${openIssues.length === 1 ? '' : 's'} found`);
  };

  const finaliseOperation = () => {
    if (!canFinalise) {
      toast.error('Cannot finalise: resolve blockers and complete required checklist first');
      return;
    }
    toast.success('Operation passed administrative guard checklist');
  };

  return (
    <div className="space-y-6 p-6">
      <section className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm">
        <div className="flex flex-col gap-5 xl:flex-row xl:items-start xl:justify-between">
          <div className="flex items-start gap-4">
            <div className="rounded-md bg-[#6c3a80]/10 p-3 text-[#6c3a80]">
              <ShieldCheck className="h-7 w-7" />
            </div>
            <div>
              <p className="text-sm font-semibold uppercase tracking-wide text-[#6c3a80]">Administrative Error Reduction</p>
              <h1 className="mt-1 text-3xl font-bold text-slate-950">Administrative Error Guard</h1>
              <p className="mt-3 max-w-3xl text-sm leading-6 text-slate-600">
                Automatic checks for client/carer validation, duplicates, missing documents, incomplete rota, invoice/visit mismatches, missing notes, unassigned tasks, expired dates and final approval readiness.
              </p>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={runScan}
              className="inline-flex h-10 items-center gap-2 rounded-md border border-slate-300 px-4 text-sm font-semibold text-slate-700 hover:bg-slate-50"
            >
              <RefreshCw className="h-4 w-4" />
              Run Scan
            </button>
            <button
              type="button"
              onClick={finaliseOperation}
              className={`inline-flex h-10 items-center gap-2 rounded-md px-4 text-sm font-semibold text-white ${canFinalise ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-slate-400'}`}
            >
              <CheckCircle2 className="h-4 w-4" />
              Finalise Operation
            </button>
          </div>
        </div>
      </section>

      <section className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        <MetricCard label="Open issues" value={String(openIssues.length)} icon={<AlertTriangle className="h-5 w-5" />} tone={openIssues.length ? 'warning' : 'success'} />
        <MetricCard label="Blockers" value={String(blockers.length)} icon={<XCircle className="h-5 w-5" />} tone={blockers.length ? 'danger' : 'success'} />
        <MetricCard label="Checklist" value={`${completedChecklist}/${finalChecklist.length}`} icon={<ClipboardCheck className="h-5 w-5" />} />
        <MetricCard label="Last scan" value={formatTime(lastScanAt)} icon={<RefreshCw className="h-5 w-5" />} />
      </section>

      <section className={`rounded-lg border p-4 ${canFinalise ? 'border-emerald-200 bg-emerald-50' : 'border-amber-200 bg-amber-50'}`}>
        <div className="flex gap-3 text-sm leading-6">
          {canFinalise ? <CheckCircle2 className="mt-0.5 h-5 w-5 flex-shrink-0 text-emerald-700" /> : <AlertTriangle className="mt-0.5 h-5 w-5 flex-shrink-0 text-amber-700" />}
          <p className={canFinalise ? 'text-emerald-950' : 'text-amber-950'}>
            {canFinalise
              ? 'All blockers are resolved and required checklist items are complete. This operation can be finalised.'
              : 'Finalisation is blocked until critical/high validation issues and required checklist items are completed.'}
          </p>
        </div>
      </section>

      <section className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <h2 className="text-xl font-bold text-slate-950">Validation Issues</h2>
          <select
            value={area}
            onChange={(event) => setArea(event.target.value as 'All' | ValidationArea)}
            className="h-10 rounded-md border border-slate-300 px-3 text-sm"
          >
            <option value="All">All areas</option>
            {Object.keys(areaIcons).filter((item) => item !== 'Final Checklist').map((item) => (
              <option key={item} value={item}>{item}</option>
            ))}
          </select>
        </div>

        <div className="mt-4 grid gap-4 xl:grid-cols-2">
          {issues.map((issue) => (
            <article key={issue.id} className={`rounded-lg border p-5 shadow-sm ${issue.status === 'resolved' ? 'border-emerald-200 bg-emerald-50' : 'border-slate-200 bg-white'}`}>
              <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                <div className="flex gap-3">
                  <div className="rounded-md bg-[#6c3a80]/10 p-3 text-[#6c3a80]">
                    {areaIcons[issue.area]}
                  </div>
                  <div>
                    <div className="flex flex-wrap items-center gap-2">
                      <h3 className="font-bold text-slate-950">{issue.title}</h3>
                      <span className={`rounded-full border px-2 py-1 text-xs font-semibold ${severityStyles[issue.severity]}`}>
                        {issue.severity}
                      </span>
                      {issue.blocker && (
                        <span className="rounded-full bg-red-100 px-2 py-1 text-xs font-semibold text-red-800">
                          blocker
                        </span>
                      )}
                    </div>
                    <p className="mt-2 text-sm leading-6 text-slate-600">{issue.description}</p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => markResolved(issue.id)}
                  className={`inline-flex h-10 items-center gap-2 rounded-md px-3 text-sm font-semibold ${issue.status === 'resolved' ? 'bg-emerald-100 text-emerald-800' : 'bg-[#6c3a80] text-white hover:bg-[#5e3474]'}`}
                >
                  {issue.status === 'resolved' ? <CheckCircle2 className="h-4 w-4" /> : <ShieldCheck className="h-4 w-4" />}
                  {issue.status === 'resolved' ? 'Resolved' : 'Mark Resolved'}
                </button>
              </div>
              <div className="mt-4 grid gap-3 text-sm md:grid-cols-2">
                <Detail label="Affected record" value={issue.affectedRecord} />
                <Detail label="Area" value={issue.area} />
              </div>
              <div className="mt-4 rounded-md bg-slate-50 p-3 text-sm leading-6 text-slate-700">
                <span className="font-semibold text-slate-950">Action:</span> {issue.action}
              </div>
            </article>
          ))}
        </div>
      </section>

      <section className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
        <div className="flex items-center gap-3">
          <ListChecks className="h-5 w-5 text-[#6c3a80]" />
          <h2 className="text-xl font-bold text-slate-950">Checklist before completing an operation</h2>
        </div>
        <div className="mt-4 grid gap-3 lg:grid-cols-2">
          {finalChecklist.map((item) => (
            <label key={item.id} className={`flex items-start gap-3 rounded-lg border p-4 ${checklist[item.id] ? 'border-emerald-200 bg-emerald-50' : 'border-slate-200 bg-white'}`}>
              <input
                type="checkbox"
                checked={Boolean(checklist[item.id])}
                onChange={() => toggleChecklist(item.id)}
                className="mt-1 h-4 w-4 rounded border-slate-300 text-[#6c3a80] focus:ring-[#6c3a80]"
              />
              <span>
                <span className="font-semibold text-slate-950">{item.label}</span>
                {item.required && <span className="ml-2 rounded-full bg-red-100 px-2 py-0.5 text-xs font-semibold text-red-800">required</span>}
                <span className="mt-1 block text-sm leading-6 text-slate-600">{item.description}</span>
              </span>
            </label>
          ))}
        </div>
      </section>
    </div>
  );
}

function MetricCard({
  label,
  value,
  icon,
  tone = 'default',
}: {
  label: string;
  value: string;
  icon: ReactNode;
  tone?: 'default' | 'success' | 'warning' | 'danger';
}) {
  const styles = {
    default: 'border-slate-200 bg-white',
    success: 'border-emerald-200 bg-emerald-50',
    warning: 'border-amber-200 bg-amber-50',
    danger: 'border-red-200 bg-red-50',
  };

  return (
    <article className={`rounded-lg border p-4 shadow-sm ${styles[tone]}`}>
      <div className="flex items-center justify-between gap-3">
        <p className="text-xs font-semibold uppercase text-slate-500">{label}</p>
        <div className="text-[#6c3a80]">{icon}</div>
      </div>
      <p className="mt-2 text-3xl font-bold text-slate-950">{value}</p>
    </article>
  );
}

function Detail({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <p className="text-xs font-semibold uppercase text-slate-500">{label}</p>
      <p className="mt-1 font-medium text-slate-950">{value}</p>
    </div>
  );
}

function formatTime(value: string) {
  return new Date(value).toLocaleTimeString('en-GB', {
    hour: '2-digit',
    minute: '2-digit',
  });
}
